import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import AppActions from '../../actions/app';
import AppStore from '../../stores/app';
import InviteStore from '../../stores/invite';
import BusinessStore from '../../stores/business';
import OrganizationStore from '../../stores/organization';

import AccountSection from '@repo/ui/components/content/onboarding/AccountSection';
import WelcomeInfoSection from '@repo/ui/components/content/onboarding/WelcomeInfoSection';
import DoubleWide from '@repo/ui/components/content/onboarding/DoubleWide';
import { api as API } from '../../mixins/api';
import LocalStorage from '../../mixins/LocalStorage';
import Auth from '../../utils/Auth';
import {
  getNextLocation,
  getOnboardingType,
  getOnboardingDirect,
  getOnboardingReferral,
} from '../../utils/LocationState';
import Link from '@repo/ui/components/buttons/Link';
import { buildReturnPath } from '@repo/utils/URLFormatters';
import Icon from '@repo/ui/components/ui/Icon';
import { emailRegex } from '@repo/utils/RegexPatterns';
import { getSocialError, handleSocialConnect } from '@/utils/SocialConnect.js';
import AlreadyLoggedIn from './AlreadyLoggedIn';
import { PageContainer } from '@repo/ui/components/layout/PageLayouts';

class Signup extends PureComponent {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state = {
    lastEmail: null,
    ssoRedirectUrl: null,
    enforceSso: false,
    loading: false,
  };

  componentDidMount() {
    const { t } = this.props;
    AppActions.changePageTitle(t('commonPlat:actions.signUp'));
  }

  handleChangeRegistration = ({ email }) => {
    if (email && this.state.lastEmail !== email && emailRegex.test(email)) {
      this.setState({ lastEmail: email });
      clearTimeout(this._emailCheckTimer);
      this._emailCheckTimer = setTimeout(() => {
        API.makeRequest(
          `/sso/tenants?email=${encodeURIComponent(email)}`,
          'GET',
          {},
          (err, res) => {
            if (!err && res && res.body) {
              let t = res.body.tenants || [];
              if (!t.length || res.body.usePasswordLogin) {
                return this.setState({ ssoRedirectUrl: null, enforceSso: false });
              }
              const sso = t[0];
              const { invite } = InviteStore.getState();
              const { location } = this.props;
              const next = getNextLocation(invite, location);
              const returnPath = buildReturnPath(next.pathname, next.query);
              this.setState({
                ssoRedirectUrl: `${import.meta.env.VITE_AUTH_HOST}/redirect/${
                  sso.type
                }?email=${encodeURIComponent(email)}&returnPath=${returnPath}`,
                enforceSso: sso.enforceSso,
              });
              return;
            }
            // Reset to nothing if we previously had a result and no longer do
            if (this.state.ssoRedirectUrl) {
              this.setState({ ssoRedirectUrl: null, enforceSso: false });
            }
          },
        );
      }, 500);
    } else {
      // Unset any previously found redirect url
      if (this.state.ssoRedirectUrl) {
        this.setState({ ssoRedirectUrl: null, enforceSso: false });
      }
    }
  };

  handleRegister = (
    { firstName, lastName, email, password, confirmPassword, captcha },
    handleServerResponse,
  ) => {
    const { i18n, t } = this.props;
    const { defaultLocation = {} } = AppStore.getState();
    let mutLocation = { ...defaultLocation };
    if (!mutLocation.city) delete mutLocation.city;
    if (!mutLocation.region) delete mutLocation.region;
    if (!mutLocation.country) delete mutLocation.country;

    if (password !== confirmPassword) {
      return handleServerResponse(() => true)(null, {
        body: { errors: [{ password: t('common:errors.passwordsDontMatch') }] },
      });
    }

    this.setState({ loading: true });

    API.makeRequest(
      '/user',
      'POST',
      {
        firstName,
        lastName,
        email,
        password,
        captcha,
        locale: i18n?.language || i18n?.resolvedLanguage,
        ...mutLocation,
      },
      handleServerResponse((response) => {
        if (response) {
          Auth.login(email, password, (loggedIn, accessToken) => {
            if (loggedIn) {
              LocalStorage.setItem('registration.method', 'email');
              LocalStorage.setItem('registration.email', email);
              // Setting these values for instant login effect without reload
              API.activeAccessToken = accessToken;
              API.accessToken = accessToken;
              if (response.user) {
                AppActions.login(response.user);

                // Provide enough time for stores to update
                setTimeout(() => {
                  this.props.router.push(
                    getNextLocation(InviteStore.getState().invite, this.props.location, 'signup'),
                  );
                }, 10);
              } else {
                alert(`There was an error fetching your profile, reload and try again.`);
                this.setState({ loading: false });
              }
            }
          });
        } else {
          this.setState({ loading: false });
        }
      }),
    );
  };

  render() {
    const { location, t } = this.props;
    const { ssoRedirectUrl, enforceSso, loading } = this.state;
    const { error } = location.query;
    const { user } = AppStore.getState();
    const { invite } = InviteStore.getState();
    const { business } = BusinessStore.getState();
    const { organization } = OrganizationStore.getState();

    const context =
      business && business.slug
        ? { ...business, type: 'business' }
        : { ...organization, type: 'organization' };

    const type = getOnboardingType(invite, location);
    const direct = getOnboardingDirect(location, context);
    const referral = getOnboardingReferral(location);
    const socialError = getSocialError(error, t);

    return (
      <PageContainer>
        {user ? (
          <AlreadyLoggedIn user={user} socialError={socialError} />
        ) : (
          <DoubleWide>
            <WelcomeInfoSection type={type} invite={invite} direct={direct} referral={referral} />
            <AccountSection
              api={API}
              socialError={socialError}
              token={location.query.token}
              state={location.state}
              loading={loading}
              nextLocation={getNextLocation(invite, location, 'signup')}
              nextLocationSocial={getNextLocation(invite, location)}
              onEmailRegister={this.handleRegister}
              onSocialConnect={handleSocialConnect}
              onChangeRegister={this.handleChangeRegistration}
              onContinueSSO={() => (window.location = ssoRedirectUrl)}
              ssoNotice={
                !!ssoRedirectUrl && (
                  <div style={{ margin: 5.5 }}>
                    <Link onClick={() => (window.location = ssoRedirectUrl)}>
                      {enforceSso ? (
                        <>{t('common:labels.thisEmailRequiresYouToContinueWithSSO')} </>
                      ) : (
                        <>{t('common:labels.thisEmailAllowsSSO')} </>
                      )}
                      <Icon name="chevron-right" />
                    </Link>
                  </div>
                )
              }
              enforceSso={enforceSso}
              hidePasswordField
              showApple
              create
            />
          </DoubleWide>
        )}
      </PageContainer>
    );
  }
}

export default withTranslation(['commonPlat', 'common'])(Signup);
